# The parameters a deep link carries, and the hash it opens — card
# 6aab72ceed72ee7fceb166c3.
#
# A mail link of the shape
#
#     /app?access_token=…&uid=…&redirectTo=long_milestone_report
#          &redirectParams=%7B%22studentId%22%3A%22…%22%7D
#
# is consumed by each shell's bootstrap: it installs the session, then asks
# `initialize` to open `redirectTo` with `redirectParams`. Every shell had its
# own copy of that handling and every copy was broken, so the two steps live
# here once and all three import them.
#
# THREE SEPARATE BREAKS, in the four lines this replaces:
#
#  1. `parseUrl` in each shell never percent-decodes, so the value reaching
#     JSON.parse was still "%7B%22studentId%22%3A%22…%22%7D". JSON.parse THREW
#     on the leading '%' every time and the catch swallowed it, so the parsed
#     object was ALWAYS {} — the mechanism had never once carried a parameter.
#  2. The shells then passed the RAW STRING on in the parsed object's place,
#     and `for key, value of` a string primitive enumerates CHARACTER INDICES.
#     The destination became
#     "#/long_milestone_report?0=%&1=7&2=B&3=%&4=2&5=2&6=s&…".
#  3. Nothing checked the shape, so any non-object — a bare string, an array, a
#     JSON literal like `"7"` or `null` — was enumerated rather than refused.
#
# The rule both functions keep: a link carrying rubbish loses its parameters
# and still opens its screen. It never builds a query out of the rubbish.

# Decode, parse, and answer with a plain object or {}.
export decodeRedirectParams = (raw) ->
  return {} unless raw?
  return raw if isPlainObject raw
  try
    decoded = decodeURIComponent "#{raw}"
  catch
    # Not valid percent-encoding (a stray '%' in the value). The text may still
    # be usable JSON — a link built without encodeURIComponent.
    decoded = "#{raw}"
  try
    parsed = JSON.parse decoded
  catch
    console.error "Ignoring bad redirect params: #{raw}"
    return {}
  unless isPlainObject parsed
    console.error "Ignoring non-object redirect params: #{raw}"
    return {}
  parsed

# `typeof null` is 'object', and an Array enumerates as indices — which is the
# exact shape defect (2) produced. Only a plain object may become a query.
isPlainObject = (value) ->
  value? and typeof value is 'object' and not Array.isArray value

# Build "#/<route>?<k>=<v>&…" for a route's own params.
#
# THE GUARD IS THE POINT. `route_params` arrives from a bootstrap that used to
# hand a string here, so the type check is not belt-and-braces — it is the
# assertion that defect (2) cannot come back through any caller, including one
# written later. A non-object contributes no query rather than its characters.
#
# Values are ENCODED. The old loop interpolated them raw, so a parameter
# holding '&', '#' or a space produced a hash the router read as a different
# route and different parameters.
export routeHash = (route_to, route_params = {}) ->
  hash = "#/#{route_to}"
  return hash unless isPlainObject route_params
  # A route_to may ALREADY carry a query: the bootstraps also call initialize
  # with `window.location.hash.substring 2`, which is the whole hash including
  # "?studentId=…" for a plain hash deep link. Appending a second '?' there
  # would produce "#/r?a=1?b=2", which the router reads as one parameter whose
  # value contains a '?'.
  separator = if route_to? and "#{route_to}".indexOf('?') >= 0 then '&' else '?'
  for key, value of route_params
    continue unless value?
    hash += "#{separator}#{encodeURIComponent key}=#{encodeURIComponent value}"
    separator = '&'
  hash

# Does this link's sign-in REPLACE the session the browser already holds? —
# card 6ac449205e862d397de34685.
#
# THE DEFECT. The bootstrap installed a link's access_token only when the
# browser held no session (or for new_user / a different studentId). A browser
# that already held ANYBODY's session kept it, and the link's redirectTo then
# ran as that old session. The mobile app's Manage Account -> Open in Browser
# link for a subscribed parent opened Safari still signed in as the account
# the app's own sign-up had finished in Safari — no students, mid-enrollment —
# so the parent landed on the enrollment form, as somebody else.
#
# THE RULE. A server-minted link names its principal (`uid`), and the link is
# the newer, explicit answer to "who is this": when that is not who the browser
# holds, the link wins, exactly as new_user always has. A link for the login
# already signed in leaves the session alone — the browser's session for the
# same person keeps its selected student. A link with no uid is compared by its
# token instead. A studentId carrying '#' is a fragment, not a link; no install.
export linkReplacesSession = (params, api) ->
  return false unless params?.access_token?
  return false if /#/.test params.studentId
  return true unless api.isLoggedIn()
  return true if params.new_user == 'true'
  return true if params.studentId? and params.studentId != api.currentStudentId
  if params.uid?
    "#{params.uid}" != "#{api.currentUserId ? ''}"
  else
    "#{params.access_token}" != "#{api.accessTokenId ? ''}"
